import { useState } from 'react';
import { useTasksStore } from '../../stores/tasks';
import type { Task } from '../../lib/api';
import { apiErrorMessage } from '../../lib/apiError';
import { parseISO, format } from 'date-fns';
import { statusLabel, type StatusValue, type TaskGroup } from './taskGroups';

// ── Inline edit drawer ─────────────────────────────────────────────────────

interface EditDrawerProps {
  task: Task;
  onClose: () => void;
}

export function EditDrawer({ task, onClose }: EditDrawerProps) {
  const updateTask = useTasksStore((s) => s.updateTask);
  const [title, setTitle]   = useState(task.title);
  const [description, setDescription] = useState(task.description ?? '');
  const [dueAt, setDueAt]   = useState(task.due_at ? task.due_at.slice(0, 10) : '');
  const [status, setStatus] = useState<StatusValue>(task.status);
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  async function save() {
    if (!title.trim()) { setErr('Title is required'); return; }
    setSaving(true);
    try {
      await updateTask(task.id, {
        title: title.trim(),
        status,
        dueAt: dueAt || null,
        description: description.trim() || null,
      });
      onClose();
    } catch (e: unknown) {
      setErr(apiErrorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    background: 'var(--vs2)', border: '1px solid var(--vb)', borderRadius: 4,
    color: 'var(--text)', fontSize: '0.8rem', padding: '6px 10px', width: '100%',
    outline: 'none', boxSizing: 'border-box',
  };

  const statusColors: Record<StatusValue, string> = {
    open: 'var(--muted)', in_progress: '#3B82F6', done: 'var(--green)',
  };

  return (
    <div style={{
      borderTop: '1px solid var(--vb)', padding: '12px 16px 14px',
      background: 'var(--slate-50)', display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {/* Title */}
      <div>
        <label style={{ fontSize: '0.65rem', color: 'var(--xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Title</label>
        <input
          value={title}
          onChange={(e) => { setTitle(e.target.value); setErr(''); }}
          style={{ ...inputStyle, marginTop: 4 }}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') onClose(); }}
          autoFocus
        />
      </div>

      {/* Details */}
      <div>
        <label style={{ fontSize: '0.65rem', color: 'var(--xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Details</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Add any extra context or notes…"
          rows={3}
          style={{ ...inputStyle, marginTop: 4, resize: 'vertical', minHeight: 60, fontFamily: 'inherit', lineHeight: 1.5 }}
          onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
        />
      </div>

      {/* Status + Due row */}
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '0.65rem', color: 'var(--xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusValue)}
            style={{ ...inputStyle, marginTop: 4, color: statusColors[status], cursor: 'pointer' }}
          >
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="done">Done</option>
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '0.65rem', color: 'var(--xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Due date</label>
          <input
            type="date"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            style={{ ...inputStyle, marginTop: 4 }}
          />
        </div>
      </div>

      {/* Meta */}
      <div style={{ fontSize: '0.67rem', color: 'var(--xs)', display: 'flex', gap: 12 }}>
        <span>Created {format(parseISO(task.created_at), 'd MMM yyyy, HH:mm')}</span>
        {task.assignee_name && <span>Assigned to <strong style={{ color: 'var(--text-muted)' }}>{task.assignee_name}</strong></span>}
        {(task.client_account_name ?? task.thread_name) && (
          <span>Thread: <strong style={{ color: 'var(--v1)' }}>{task.client_account_name ?? task.thread_name}</strong></span>
        )}
      </div>

      {err && <div style={{ fontSize: '0.75rem', color: 'var(--red)' }}>{err}</div>}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{
          background: 'transparent', border: '1px solid var(--vb)', borderRadius: 4,
          color: 'var(--muted)', fontSize: '0.75rem', padding: '5px 14px', cursor: 'pointer',
        }}>
          Cancel
        </button>
        <button onClick={save} disabled={saving} style={{
          background: saving ? 'var(--vb2)' : 'var(--v1)', border: 'none', borderRadius: 4,
          color: saving ? 'var(--muted)' : 'var(--text-on-accent)', fontSize: '0.75rem', fontWeight: 700, padding: '5px 16px',
          cursor: saving ? 'wait' : 'pointer',
        }}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── Task detail body (edit form for assignees, read-only info for everyone else) ──

export function TaskDetailBody({ group, currentUserId, onClose }: {
  group: TaskGroup; currentUserId: string; onClose: () => void;
}) {
  const myTask = group.tasks.find((t) => t.assigned_to === currentUserId);
  const repr = group.representative;
  const contextLabel = repr.client_account_name ?? repr.thread_name;

  if (myTask) return <EditDrawer task={myTask} onClose={onClose} />;

  return (
    <div style={{
      borderTop: '1px solid var(--vb)', padding: '10px 16px',
      background: 'var(--slate-50)', display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ fontSize: '0.67rem', color: 'var(--xs)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <span>Created {format(parseISO(repr.created_at), 'd MMM yyyy, HH:mm')}</span>
        {group.tasks.length > 0 && (
          <span>Assigned to <strong style={{ color: 'var(--text-muted)' }}>
            {group.tasks.map((t) => t.assignee_name ?? 'Unknown').join(', ')}
          </strong></span>
        )}
        {contextLabel && <span>Thread: <strong style={{ color: 'var(--v1)' }}>{contextLabel}</strong></span>}
      </div>
      {repr.description && (
        <div style={{ fontSize: '0.72rem', color: 'var(--text)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
          {repr.description}
        </div>
      )}
      {group.tasks.length > 1 && (
        <div style={{ fontSize: '0.67rem', color: 'var(--xs)', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {group.tasks.map((t) => (
            <span key={t.id}>
              <span style={{
                display: 'inline-block', width: 7, height: 7, borderRadius: '50%', marginRight: 5,
                background: t.status === 'done' ? 'var(--green)' : t.status === 'in_progress' ? '#3B82F6' : 'var(--vb2)',
              }} />
              {t.assignee_name ?? 'Unknown'} — {statusLabel(t.status)}
            </span>
          ))}
        </div>
      )}
      <div style={{ fontSize: '0.72rem', color: '#92400E', fontStyle: 'italic' }}>
        Only assignees can edit their own task
      </div>
    </div>
  );
}
